/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.
*/

import {
	ChangeDetectorRef,
	Component,
	ContentChild,
	ContentChildren,
	type OutputRefSubscription,
	type QueryList,
	TemplateRef,
	contentChildren,
	effect,
	inject,
	input,
	output,
	signal,
	viewChild,
	viewChildren,
} from "@angular/core";
import { ActionLoggerService } from "@app/@services/generic/action-logger.service";
import type { CbSubmitEvent } from "@app/widgets/base/button/button.component";
import { TranslateService } from "@ngx-translate/core";
import { Subject, take, tap } from "rxjs";
import type { FileComponent } from "../../file/file.component";
import { FormControlsComponent } from "../form-controls/form-controls.component";
import type { CbFormGroup } from "../form-group";
import { FormGroupComponent } from "../form-group/form-group.component";
import { FormInputComponent } from "../form-input/form-input/form-input.component";
import {
	FORM_ITEMS_COMPONENTS,
	FormItemComponent,
} from "../form-input/form-item/form-item.component";
import { ItemTemplateDirective } from "../form-input/item-template.directive";
import { GlobalFormControlsComponent } from "../global-form-controls/global-form-controls.component";

export interface CbFormSubmitEvent {
	success: Subject<void>;
	error: Subject<
		boolean | string | { message: string; args: Record<string, unknown> } | null
	>;
	reset: Subject<void>;
}

@Component({
	selector: "cb-form",
	templateUrl: "./form.component.html",
	styleUrl: "./form.component.scss",
	standalone: false,
	//encapsulation: ViewEncapsulation.None,
	//changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormComponent {
	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
	private readonly translate: TranslateService = inject(TranslateService);
	private readonly actionLogger: ActionLoggerService =
		inject(ActionLoggerService);

	public readonly standardInputs: string[] = Object.keys(FORM_ITEMS_COMPONENTS);

	public globalError = signal<string | undefined>(undefined);

	public requiredFields: string[] = [];
	public disabledFields: string[] = [];

	public readonly formGroup = input.required<CbFormGroup>();

	public readonly globalErrorPosition = input<"top" | "bottom">("bottom");

	public readonly onSubmit = output<CbFormSubmitEvent>();

	public readonly itemTemplateRef = viewChild.required(ItemTemplateDirective, {
		read: TemplateRef,
	});

	public readonly formInputs = viewChildren(FormInputComponent);

	public readonly formGroups = contentChildren(FormGroupComponent);

	public readonly formItems = contentChildren(FormItemComponent);

	@ContentChildren(FormControlsComponent)
	public set formControls(value: QueryList<FormControlsComponent>) {
		if (!value || value.length === 0) return;

		this.localControls.set(value);

		this.controlsDestroy$.next();
		this.controlsDestroy$.complete();

		this.controlsDestroy$ = new Subject<void>();

		for (const btn of value) {
			btn.submit.subscribe((event: CbSubmitEvent) => {
				this.formSubmit(event);
			});
		}
	}

	@ContentChild(GlobalFormControlsComponent)
	public set globalFormControls(value: GlobalFormControlsComponent) {
		if (!value) return;

		this.globalControls.set(value);

		this.controlsDestroy$.next();
		this.controlsDestroy$.complete();

		this.controlsDestroy$ = new Subject<void>();

		if (this.submitSub) {
			this.submitSub.unsubscribe();
		}

		this.submitSub = value.submit.subscribe((event: CbSubmitEvent) => {
			this.formSubmit(event);
		});
	}

	protected localControls = signal<
		QueryList<FormControlsComponent> | undefined
	>(undefined);
	protected globalControls = signal<GlobalFormControlsComponent | undefined>(
		undefined,
	);

	private submitSub?: OutputRefSubscription;
	private controlsDestroy$ = new Subject<void>();

	constructor() {
		effect(() => {
			for (const controlName in this.formGroup().form) {
				const control = this.formGroup().form[controlName];
				if ("required" in control && control.required) {
					this.requiredFields.push(controlName);
				}
			}
		});
	}

	ngOnInit() {
		/* this.formGroup().valueChanges.subscribe(() => {
			this.cdr.detectChanges();
		}); */
	}

	public async formSubmit(event: CbSubmitEvent) {
		this.formGroup().markAllAsTouched();

		if (!this.formGroup().valid) {
			event.error.next(null);
			return;
		}

		const successEvent = new Subject<void>();
		const errorEvent = new Subject<
			| boolean
			| string
			| { message: string; args: Record<string, unknown> }
			| null
		>();

		await this.uploadFiles();

		this.actionLogger.registerAction("Form submit", {
			formGroup: this.formGroup().valuesObject,
		});

		successEvent
			.pipe(
				take(1),
				tap(() => {
					event.success.next();
				}),
			)
			.subscribe();

		errorEvent
			.pipe(
				take(1),
				tap((error) => {
					event.error.next(error);
					if (typeof error === "string") {
						this.globalError.set(this.translate.instant(error));
					} else if (typeof error === "object") {
						if (error) {
							this.globalError.set(
								this.translate.instant(error.message, error.args),
							);
						}
					} else {
						this.globalError.set(
							this.translate.instant("COMMON.FORM_ERRORS.GENERIC"),
						);
					}

					if (this.globalError()) {
						this.actionLogger.registerAction("Form submit error", {
							error: this.globalError(),
						});
					}
				}),
			)
			.subscribe();

		this.onSubmit.emit({
			success: successEvent,
			error: errorEvent,
			reset: new Subject<void>(),
		});
	}

	public async uploadFiles(): Promise<void> {
		const promises: Promise<void>[] = [];
		for (const input of this.formInputs()) {
			if (input.data()?.type === "file") {
				const instance = input.componentRef()?.instance as FileComponent;

				if (instance.uploadBehavior() === "onSubmit") {
					promises.push(
						(input.componentRef()?.instance as FileComponent).flush(),
					);
				}
			}
		}
		await Promise.all(promises);
	}

	public addArrayRow(arrayControlName: string): void {
		this.formGroup().addArrayRow(arrayControlName);
		this.cdr.detectChanges();
	}

	public removeArrayRow(arrayControlName: string, index: number): void {
		this.formGroup().removeArrayRow(arrayControlName, index);
		this.cdr.detectChanges();
	}

	public getArrayMaxRows(name: string): number {
		return this.formGroup().getArrayMaxRows(name);
	}

	ngOnDestroy() {
		this.controlsDestroy$.next();
		this.controlsDestroy$.complete();

		this.formGroup().destroy();
	}
}
